import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AreaService,
  DepartamentService,
  DistrictService,
  JobOfferService,
  JobTitleService,
  JobTypeService,
  ProvinceService,
} from 'src/app/api/services';
import { ProfesionService } from 'src/app/api/services/proffesion.service';
import { JobOfferForm } from 'src/app/shared/form/job-offer-form.model';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import * as ClassicEditorImport from '@ckeditor/ckeditor5-build-classic';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroInformationCircle } from '@ng-icons/heroicons/outline';
import { ToastService } from 'src/app/shared/service/toast.service';
import { EducationService } from 'src/app/api/services/education.service';
import { EducationDegreeService } from 'src/app/api/services/education-degree.service';
import { MESSAGES } from 'src/app/core/constant/messages';
import { LoadingButtonComponent } from 'src/app/shared/component/loading-button/loading-button.component';
import { InputTextComponent } from 'src/app/shared/component/input-text/input-text.component';
import { InputSelectComponent } from 'src/app/shared/component/input-select/input-select.component';
import { NavButtonComponent } from 'src/app/shared/component/nav-button/nav-button.component';
import { CompanyPackage } from 'src/app/shared/model/package';
import { CompaniaPaqueteService } from 'src/app/api/services/company-package.service';
import { switchMap } from 'rxjs';
import { LoadingComponent } from 'src/app/shared/component/loading/loading.component';
import { BaseFormComponent } from 'src/app/shared/form/base-form.component';

const ClassicEditor =
  (ClassicEditorImport as any).default || ClassicEditorImport;

@Component({
  selector: 'app-create-job-offer',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CKEditorModule,
    NgIcon,
    LoadingButtonComponent,
    InputTextComponent,
    InputSelectComponent,
    NavButtonComponent,
    LoadingComponent,
  ],
  templateUrl: './create-job-offer.component.html',
  styleUrls: ['./create-job-offer.component.css'],
  viewProviders: [provideIcons({ heroInformationCircle })],
})
export class CreateJobOfferComponent
  extends BaseFormComponent
  implements OnInit
{
  public Editor: any = ClassicEditor;
  public content: string = '';

  public editorConfig = {
    placeholder: 'Escribe aquí...',
    toolbar: [
      'heading',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'bulletedList',
      '|',
      'outdent',
      'indent',
      '|',
      'link',
      'insertTable',
      '|',
      'undo',
      'redo',
    ],
  };

  isSubmitting: boolean = false;
  isLoading: boolean = true;
  jobOfferForm: JobOfferForm;
  companyId!: string;
  departaments!: any[];
  districts!: any[];
  provinces!: any[];
  areas!: any[];
  employmentTypes!: any[];
  jobTitles!: any[];
  professions!: any[];
  educations: any[] = [];
  educationsDegreee: any[] = [];
  packages: CompanyPackage[] = [];
  selectedPackage?: CompanyPackage;

  constructor(
    private fb: FormBuilder,
    private departamentService: DepartamentService,
    private provinceService: ProvinceService,
    private districtService: DistrictService,
    private areaService: AreaService,
    private employmentService: JobTypeService,
    private jobTitleService: JobTitleService,
    private profesionService: ProfesionService,
    private route: ActivatedRoute,
    private jobOfferService: JobOfferService,
    toastService: ToastService,
    private educationService: EducationService,
    private educationDegreeService: EducationDegreeService,
    private companyPackageService: CompaniaPaqueteService,
    private router: Router
  ) {
    super(toastService);
    this.jobOfferForm = new JobOfferForm(this.fb);
  }

  ngOnInit(): void {
    this.route.parent?.params
      .pipe(
        switchMap((params) => {
          this.companyId = params['id'];
          return this.companyPackageService.getAvailableByIdCompany(
            this.companyId
          );
        })
      )
      .subscribe({
        next: (res) => {
          this.packages = res.dto.packages;
          this.isLoading = false;
        },
        error: (err) => {
          this.toastService.showMessages(
            'error',
            err.error?.listMessage || [MESSAGES.EDUCATION.GET_ALL_ERROR]
          );
          this.isLoading = false;
        },
      });

    this.educationService.getEducations().subscribe({
      next: (res) => {
        this.educations = res.dto.educations.map((item: any) => ({
          value: item.idEducation,
          label: item.name,
        }));
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.EDUCATION.GET_ALL_ERROR]
        );
      },
    });

    this.departamentService.departamentoGet().subscribe({
      next: (data) => {
        this.departaments = data.dto.departaments.map((item: any) => ({
          value: item.idDepartament,
          label: item.name,
        }));
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.DEPARTAMENT.GET_ALL_ERROR]
        );
      },
    });

    this.areaService.areaGet().subscribe({
      next: (data) => {
        this.areas = data.dto.areas.map((item: any) => ({
          value: item.idArea,
          label: item.name,
        }));
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.AREA.GET_ALL_ERROR]
        );
      },
    });

    this.employmentService.tipoEmpleoGet().subscribe({
      next: (data) => {
        this.employmentTypes = data.dto.jobTypes.map((item: any) => ({
          value: item.id,
          label: item.description,
        }));
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.JOB_TYPE.GET_ALL_ERROR]
        );
      },
    });

    this.jobTitleService.tituloEmpleoGet().subscribe({
      next: (data) => {
        this.jobTitles = data.dto.jobTitles.map((item: any) => ({
          value: item.idJobTitle,
          label: item.name,
        }));
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.JOB_TITLE.GET_ALL_ERROR]
        );
      },
    });

    this.profesionService.getProfesions().subscribe({
      next: (data) => {
        this.professions = data.dto.professions.map((item: any) => ({
          value: item.idProfession,
          label: item.name,
        }));
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.PROFESSION.GET_ALL_ERROR]
        );
      },
    });
  }

  onSubmit(): void {
    if (!this.validateForm(this.jobOfferForm.form)) return;

    this.isSubmitting = true;
    const formValue = this.jobOfferForm.form.value;
    const minSalary = formValue.hasRemuneration
      ? Number(formValue.minSalary)
      : 0;
    const maxSalary = formValue.hasRemuneration
      ? Number(formValue.maxSalary)
      : 0;

    const payload = {
      idCompanyPackage: this.selectedPackage?.idCompanyPackage,
      idArea: formValue.idArea,
      idDistrict: formValue.idDistrict,
      idJobType: formValue.idJobType,
      idProfession: formValue.idProfession,
      idJobTitle: formValue.idJobTitle,
      idEducation: formValue.idEducation,
      title: formValue.jobTitle,
      subTitle: formValue.subTitle,
      description: this.content,
      minimumSalary: minSalary,
      maximumSalary: maxSalary,
      showSalary: formValue.hasRemuneration
        ? formValue.showSalary
          ? 1
          : 0
        : 0,
      numberVacancies: formValue.numberVacancies,
      minimumAge: formValue.minAge,
      maximumAge: formValue.maxAge,
      minimumExperience: formValue.experience,
      requestRemuneration: formValue.hasRemuneration ? 1 : 0,
      jobOfferRequirement: [],
    };

    this.jobOfferService.createJobOffer(payload).subscribe({
      next: () => {
        this.toastService.showMessages('success', [
          MESSAGES.JOB_OFFER.CREATE_SUCCESS,
        ]);
        this.router.navigate([`/user/company/${this.companyId}/job-offers`]);
        this.isSubmitting = false;
      },
      error: (error) => {
        this.toastService.showMessages(
          'error',
          error.error?.listMessage || [MESSAGES.JOB_OFFER.CREATE_ERROR]
        );
        this.isSubmitting = false;
        console.error(error);
      },
    });
  }

  loadProvinces(idDepartament: string): Promise<void> {
    return new Promise((resolve) => {
      this.provinceService
        .provinciaDepartamentoIdDepartamentGet({ idDepartament })
        .subscribe({
          next: (res) => {
            this.provinces = res?.dto?.provinces.map((item: any) => ({
              value: item.id,
              label: item.name,
            }));
            resolve();
          },
          error: () => resolve(),
        });
    });
  }

  loadDistricts(idProvince: string): Promise<void> {
    return new Promise((resolve) => {
      this.districtService
        .distritoProvinciaIdProvinceGet({ idProvince })
        .subscribe({
          next: (res) => {
            this.districts = res?.dto?.districts.map((item: any) => ({
              value: item.idDistrict,
              label: item.name,
            }));
            resolve();
          },
          error: () => resolve(),
        });
    });
  }

  async onDepartmentChange(idDepartament: string) {
    this.jobOfferForm.form.get('idProvince')?.reset(null, { emitEvent: false });
    this.jobOfferForm.form.get('idDistrict')?.reset(null, { emitEvent: false });
    this.provinces = [];
    this.districts = [];
    if (idDepartament) {
      this.jobOfferForm.form.get('idProvince')?.disable();
      await this.loadProvinces(idDepartament);
      this.jobOfferForm.form.get('idProvince')?.enable();
    } else {
      this.jobOfferForm.form.get('idProvince')?.disable();
      this.jobOfferForm.form.get('idDistrict')?.disable();
    }
  }

  async onProvinceChange(idProvince: string) {
    this.jobOfferForm.form.get('idDistrict')?.reset(null, { emitEvent: false });
    this.districts = [];
    if (idProvince) {
      this.jobOfferForm.form.get('idDistrict')?.disable();
      await this.loadDistricts(idProvince);
      this.jobOfferForm.form.get('idDistrict')?.enable();
    } else {
      this.jobOfferForm.form.get('idDistrict')?.disable();
    }
  }

  selectPackage(pkg: CompanyPackage) {
    this.selectedPackage = pkg;
  }

  changePackage() {
    this.selectedPackage = undefined;
  }

  get hasRemuneration(): boolean {
    return this.jobOfferForm.form.get('hasRemuneration')?.value;
  }

  get routeBack(): string {
    return `/user/company/${this.companyId}/job-offers`;
  }
}
